import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const TONES = [
  { id: "warm", label: "Warm" },
  { id: "formal", label: "Formal" },
  { id: "celebratory", label: "Celebratory" },
  { id: "brief", label: "Brief" },
];
const LENGTHS = [
  { id: "short", label: "Short" },
  { id: "medium", label: "Medium" },
  { id: "long", label: "Long" },
];

export default function NewObituary() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    deceased_name: "", born: "", born_place: "", died: "", died_place: "",
    tone: "warm", length: "medium",
    family: "", preceded_by: "",
    funeral_home_name: "", funeral_home_address: "",
    private_viewing: "", wake_viewing: "", funeral_service_time: "",
    interment: "", repass: "", repass_time: "",
    pallbearers: "", honorary_pallbearers: "", flower_bearers: "",
    details: "",
    is_pet: false,
    theme: "classic",
    funeral_date: "",
    family_contact_email: "",
    public_enabled: false,
    cover_heading: "In Loving Memory",
    badge_medallion: false,
    cover_photo_index: null,  // index into `photos[]` to use as front-cover hero (null = use portrait_b64)
  });
  const [body, setBody] = useState("");
  const [eulogy, setEulogy] = useState("");
  const [portraitB64, setPortraitB64] = useState(null);
  const [coverBgB64, setCoverBgB64] = useState(null);
  const [coverBgBusy, setCoverBgBusy] = useState(false);
  const [portraitDesc, setPortraitDesc] = useState("");
  const [referenceB64, setReferenceB64] = useState(null);  // real photo of the deceased
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [portraitBusy, setPortraitBusy] = useState(false);
  const [eulogyBusy, setEulogyBusy] = useState(false);
  const [trBusy, setTrBusy] = useState(false);
  const [trLang, setTrLang] = useState("Spanish");
  const [restoreBusy, setRestoreBusy] = useState(false);

  const themeOptions = [
    { id: "classic", label: "Classic", color: "#D4AF37" },
    { id: "heavenly", label: "Heavenly", color: "#3a7bd5" },
    { id: "faith", label: "Faith", color: "#6b3a8a" },
    { id: "scripture", label: "Scripture", color: "#a8924a" },
    { id: "celebration", label: "Celebration", color: "#c45a2e" },
    { id: "military", label: "Military", color: "#1e3a5c" },
    { id: "military_navy", label: "Navy", color: "#0a2a4a" },
    { id: "military_army", label: "Army", color: "#3a4a1a" },
    { id: "military_marines", label: "Marines", color: "#7a2a2a" },
    { id: "garden", label: "Garden", color: "#3a7a4a" },
    { id: "rose", label: "Rose", color: "#c84a6a" },
    { id: "sunset", label: "Sunset", color: "#e07a4a" },
    { id: "ocean", label: "Ocean", color: "#2e8aa0" },
    { id: "starry_night", label: "Starry Night", color: "#6a5aab" },
    { id: "vintage", label: "Vintage", color: "#a8804a" },
    { id: "pet_paw", label: "Pet Memorial", color: "#a8804a" },
    { id: "minimalist", label: "Minimalist", color: "#666666" },
    { id: "blank", label: "Blank (designer)", color: "#444444" },
  ];

  const generate = async () => {
    if (!form.deceased_name.trim()) { toast.error("Please enter the deceased's name."); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post("/ai/obituary", form);
      setBody(data.text);
      toast.success("Draft ready.");
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally {
      setGenBusy(false);
    }
  };

  const generatePortrait = async () => {
    if (!portraitDesc.trim() && !referenceB64) { toast.error("Add a reference photo or a brief description."); return; }
    setPortraitBusy(true);
    try {
      const { data } = await api.post("/ai/portrait", { description: portraitDesc, reference_b64: referenceB64 });
      setPortraitB64(data.image_b64);
      toast.success("Portrait created.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portrait failed.");
    } finally {
      setPortraitBusy(false);
    }
  };

  const onReferencePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setReferenceB64(String(r.result).split(",")[1]);
    r.readAsDataURL(f);
  };

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = files.map(f => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(",")[1]);
      r.onerror = rej;
      r.readAsDataURL(f);
    }));
    const results = await Promise.all(readers);
    setPhotos([...photos, ...results]);
  };

  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const generateEulogy = async () => {
    if (!form.deceased_name.trim()) { toast.error("Add the name first."); return; }
    setEulogyBusy(true);
    try {
      const { data } = await api.post("/ai/eulogy", {
        deceased_name: form.deceased_name,
        relationship: "family member",
        tone: form.tone === "celebratory" ? "celebratory" : "loving",
        length: form.length,
        stories: form.details,
        family_name: "",
      });
      setEulogy(data.text);
      toast.success("Eulogy composed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eulogy generation failed."); }
    finally { setEulogyBusy(false); }
  };

  const translate = async () => {
    if (!body.trim()) { toast.error("Generate the obituary first."); return; }
    setTrBusy(true);
    try {
      const { data } = await api.post("/extras/translate", { text: body, target_language: trLang });
      setBody(data.text);
      toast.success(`Translated to ${trLang}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Translate failed."); }
    finally { setTrBusy(false); }
  };

  const restoreReference = async () => {
    if (!referenceB64) { toast.error("Upload a reference photo first."); return; }
    setRestoreBusy(true);
    try {
      const { data } = await api.post("/extras/photo-restore", { image_b64: referenceB64, notes: "restore + gentle colorize" });
      setReferenceB64(data.image_b64);
      toast.success("Photo restored.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed."); }
    finally { setRestoreBusy(false); }
  };

  const generateCoverBackground = async () => {
    setCoverBgBusy(true);
    try {
      const { data } = await api.post("/ai/cover-background", {
        theme: form.theme || "classic",
        deceased_name: form.deceased_name,
        custom_prompt: "",
      });
      setCoverBgB64(data.image_b64);
      toast.success("Cover background ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate cover background.");
    } finally {
      setCoverBgBusy(false);
    }
  };

  const save = async () => {
    if (!body.trim()) { toast.error("Body is empty."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/obituaries", {
        deceased_name: form.deceased_name,
        body,
        eulogy,
        portrait_b64: portraitB64,
        photos,
        cover_photo_index: form.cover_photo_index,
        born: form.born, born_place: form.born_place,
        died: form.died, died_place: form.died_place,
        funeral_home_name: form.funeral_home_name,
        funeral_home_address: form.funeral_home_address,
        private_viewing: form.private_viewing,
        wake_viewing: form.wake_viewing,
        funeral_service_time: form.funeral_service_time,
        interment: form.interment, repass: form.repass, repass_time: form.repass_time,
        pallbearers: form.pallbearers,
        honorary_pallbearers: form.honorary_pallbearers,
        flower_bearers: form.flower_bearers,
        theme: form.theme,
        cover_heading: form.cover_heading,
        cover_background_b64: coverBgB64,
        badge_medallion: form.badge_medallion,
        is_pet: form.is_pet,
        funeral_date: form.funeral_date,
        family_contact_email: form.family_contact_email,
        public_enabled: form.public_enabled,
      });
      toast.success("Obituary saved.");
      nav(`/obituary/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 920, margin: "30px auto", padding: "0 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 6 }}>NEW OBITUARY</div>
        <h1 className="serif" style={{ fontSize: 38, color: "#fff", margin: 0, fontWeight: 400 }}>Compose a remembrance</h1>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
        {[1, 2, 3].map(s => (
          <div key={s} style={{
            padding: "8px 16px", borderRadius: 999,
            background: step === s ? "var(--gold)" : "var(--bg-3)",
            color: step === s ? "#0a0a0b" : "var(--text-muted)",
            fontSize: 13, fontWeight: 600,
            border: "1px solid " + (step === s ? "var(--gold)" : "var(--border)"),
          }}>{s}. {s === 1 ? "Details" : s === 2 ? "Refine" : "Photos & Save"}</div>
        ))}
      </div>

      {step === 1 && (
        <div className="bg-card" style={{ padding: 28 }}>
          {/* Pet mode toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22, padding: 14, background: "#0a0a0b", border: "1px solid #1f1f1f", borderRadius: 10 }}>
            <span style={{ fontSize: 22 }}>{form.is_pet ? "🐾" : "✦"}</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#fff", fontSize: 14 }}>{form.is_pet ? "Pet memorial mode" : "Human obituary mode"}</div>
              <div className="muted" style={{ fontSize: 11 }}>{form.is_pet ? "AI prompts and PDF wording will reflect a beloved companion." : "Toggle on for a beloved pet's memorial."}</div>
            </div>
            <button data-testid="pet-toggle" onClick={() => setForm({ ...form, is_pet: !form.is_pet })} className="btn-ghost" style={{ fontSize: 12 }}>
              {form.is_pet ? "Switch to human" : "Switch to pet"}
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 18 }}>
            <div>
              <label className="label-dark">Full name *</label>
              <input data-testid="new-name" className="input-dark" value={form.deceased_name} onChange={e => setForm({ ...form, deceased_name: e.target.value })} placeholder="e.g., Margaret Anne Thompson" />
            </div>
            <div>
              <label className="label-dark">Date of birth</label>
              <input data-testid="new-born" className="input-dark" value={form.born} onChange={e => setForm({ ...form, born: e.target.value })} placeholder="March 14, 1942" />
            </div>
            <div>
              <label className="label-dark">Place of birth</label>
              <input data-testid="new-born-place" className="input-dark" value={form.born_place} onChange={e => setForm({ ...form, born_place: e.target.value })} placeholder="Memphis, Tennessee" />
            </div>
            <div>
              <label className="label-dark">Date of death</label>
              <input data-testid="new-died" className="input-dark" value={form.died} onChange={e => setForm({ ...form, died: e.target.value })} placeholder="December 2, 2025" />
            </div>
            <div>
              <label className="label-dark">Place of death</label>
              <input data-testid="new-died-place" className="input-dark" value={form.died_place} onChange={e => setForm({ ...form, died_place: e.target.value })} placeholder="Dallas, Texas" />
            </div>
          </div>
          <label className="label-dark">Tone</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {TONES.map(t => (
              <button key={t.id} data-testid={`tone-${t.id}`} onClick={() => setForm({ ...form, tone: t.id })} style={{
                padding: "10px 18px", borderRadius: 999,
                background: form.tone === t.id ? "var(--gold)" : "transparent",
                color: form.tone === t.id ? "#0a0a0b" : "var(--silver)",
                border: "1px solid " + (form.tone === t.id ? "var(--gold)" : "var(--border)"),
                cursor: "pointer", fontFamily: "inherit", fontSize: 14,
              }}>{t.label}</button>
            ))}
          </div>
          <label className="label-dark">Length</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {LENGTHS.map(t => (
              <button key={t.id} data-testid={`length-${t.id}`} onClick={() => setForm({ ...form, length: t.id })} style={{
                padding: "10px 18px", borderRadius: 999,
                background: form.length === t.id ? "var(--gold)" : "transparent",
                color: form.length === t.id ? "#0a0a0b" : "var(--silver)",
                border: "1px solid " + (form.length === t.id ? "var(--gold)" : "var(--border)"),
                cursor: "pointer", fontFamily: "inherit", fontSize: 14,
              }}>{t.label}</button>
            ))}
          </div>
          <label className="label-dark">Family — spouse, children, parents, grandchildren, dear friends</label>
          <textarea data-testid="new-family" className="input-dark" value={form.family} onChange={e => setForm({ ...form, family: e.target.value })} rows={3} placeholder="e.g., Survived by her husband Robert; children Sarah (David) and Michael (Anna); five grandchildren; sister Linda." style={{ resize: "vertical", marginBottom: 16 }} />

          <label className="label-dark">Preceded in death by</label>
          <textarea data-testid="new-preceded" className="input-dark" value={form.preceded_by} onChange={e => setForm({ ...form, preceded_by: e.target.value })} rows={2} placeholder="e.g., Parents John and Mary; brother Thomas; infant son Daniel." style={{ resize: "vertical", marginBottom: 16 }} />

          <label className="label-dark">Life details — hobbies, achievements, character</label>
          <textarea data-testid="new-details" className="input-dark" value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} rows={5} placeholder="A nurse for 38 years. Loved gardening, choral music, and her grandchildren." style={{ resize: "vertical", marginBottom: 16 }} />

          <div className="serif" style={{ fontSize: 18, color: "var(--gold)", marginTop: 8, marginBottom: 12 }}>Service & Viewing Information</div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 14 }}>All optional. Fill in what applies — these will be printed in the booklet and shown on the memorial page.</div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 16 }}>
            <div>
              <label className="label-dark">Funeral home / chapel name</label>
              <input data-testid="new-funeral-home-name" className="input-dark" value={form.funeral_home_name} onChange={e => setForm({ ...form, funeral_home_name: e.target.value })} placeholder="e.g., Smith Family Funeral Home" />
            </div>
            <div>
              <label className="label-dark">Funeral home address</label>
              <input data-testid="new-funeral-home-address" className="input-dark" value={form.funeral_home_address} onChange={e => setForm({ ...form, funeral_home_address: e.target.value })} placeholder="e.g., 142 Maple Ave, Dallas, TX 75201" />
            </div>
            <div>
              <label className="label-dark">Private viewing (family only)</label>
              <input data-testid="new-private-viewing" className="input-dark" value={form.private_viewing} onChange={e => setForm({ ...form, private_viewing: e.target.value })} placeholder="e.g., Fri Mar 14, 5:00–6:00 PM" />
            </div>
            <div>
              <label className="label-dark">Wake / public viewing</label>
              <input data-testid="new-wake-viewing" className="input-dark" value={form.wake_viewing} onChange={e => setForm({ ...form, wake_viewing: e.target.value })} placeholder="e.g., Fri Mar 14, 6:00–9:00 PM" />
            </div>
            <div>
              <label className="label-dark">Funeral service time</label>
              <input data-testid="new-funeral-service-time" className="input-dark" value={form.funeral_service_time} onChange={e => setForm({ ...form, funeral_service_time: e.target.value })} placeholder="e.g., Sat Mar 15, 10:00 AM" />
            </div>
            <div>
              <label className="label-dark">Interment / inurnment location</label>
              <input data-testid="new-interment" className="input-dark" value={form.interment} onChange={e => setForm({ ...form, interment: e.target.value })} placeholder="e.g., Restland Memorial Park, Dallas, TX" />
            </div>
            <div>
              <label className="label-dark">Repass / reception location</label>
              <input data-testid="new-repass" className="input-dark" value={form.repass} onChange={e => setForm({ ...form, repass: e.target.value })} placeholder="e.g., First Baptist Fellowship Hall" />
            </div>
            <div>
              <label className="label-dark">Repass time</label>
              <input data-testid="new-repass-time" className="input-dark" value={form.repass_time} onChange={e => setForm({ ...form, repass_time: e.target.value })} placeholder="e.g., 2:00 PM" />
            </div>
          </div>

          <label className="label-dark">Honorary pallbearers (one per line)</label>
          <textarea data-testid="new-honorary" className="input-dark" rows={2} value={form.honorary_pallbearers} onChange={e => setForm({ ...form, honorary_pallbearers: e.target.value })} placeholder={"Robert Anne Thompson\nLinda Marshall\n…"} style={{ resize: "vertical", marginBottom: 14 }} />

          <label className="label-dark">Pallbearers (one per line)</label>
          <textarea data-testid="new-pallbearers" className="input-dark" rows={3} value={form.pallbearers} onChange={e => setForm({ ...form, pallbearers: e.target.value })} placeholder={"James Thompson\nDavid Lee\nMichael Carter\n…"} style={{ resize: "vertical", marginBottom: 14 }} />

          <label className="label-dark">Flower bearers (one per line)</label>
          <textarea data-testid="new-flower" className="input-dark" rows={2} value={form.flower_bearers} onChange={e => setForm({ ...form, flower_bearers: e.target.value })} placeholder={"Sarah Thompson\nAnna Greene\n…"} style={{ resize: "vertical", marginBottom: 22 }} />

          <button data-testid="new-generate" onClick={generate} disabled={genBusy} className="btn-gold" style={{ padding: "14px 30px" }}>
            {genBusy ? "Writing…" : "Generate with AI →"}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-card" style={{ padding: 28 }}>
          <label className="label-dark">Edit your obituary</label>
          <textarea data-testid="edit-body" className="input-dark" value={body} onChange={e => setBody(e.target.value)} rows={18} style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 17, lineHeight: 1.7, resize: "vertical" }} />

          {/* Translate */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
            <select data-testid="translate-lang" value={trLang} onChange={e => setTrLang(e.target.value)} className="input-dark" style={{ padding: "10px 12px", maxWidth: 200 }}>
              {["English","Spanish","French","Portuguese","Mandarin Chinese","German","Italian","Korean","Vietnamese","Tagalog","Arabic"].map(l => <option key={l}>{l}</option>)}
            </select>
            <button data-testid="translate-btn" onClick={translate} disabled={trBusy} className="btn-ghost">{trBusy ? "Translating…" : `Translate to ${trLang}`}</button>
          </div>

          {/* Eulogy generator */}
          <div style={{ marginTop: 24, padding: 18, background: "#0a0a0b", border: "1px solid #1f1f1f", borderRadius: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div>
                <div className="serif" style={{ fontSize: 18, color: "#fff" }}>Eulogy speech</div>
                <div className="muted" style={{ fontSize: 12 }}>A heartfelt speech for the family to deliver at the service.</div>
              </div>
              <button data-testid="eulogy-generate" onClick={generateEulogy} disabled={eulogyBusy} className="btn-gold" style={{ fontSize: 13 }}>{eulogyBusy ? "Composing…" : "Generate Eulogy"}</button>
            </div>
            {eulogy && (
              <textarea data-testid="eulogy-body" className="input-dark" value={eulogy} onChange={e => setEulogy(e.target.value)} rows={10} style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 16, lineHeight: 1.7, resize: "vertical", marginTop: 10 }} />
            )}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            <button onClick={() => setStep(1)} className="btn-ghost">← Back</button>
            <button onClick={generate} disabled={genBusy} className="btn-ghost">{genBusy ? "Regenerating…" : "↺ Regenerate"}</button>
            <button data-testid="new-continue-photos" onClick={() => setStep(3)} className="btn-gold">Continue → Photos & Theme</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-card" style={{ padding: 28 }}>
          <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 12 }}>AI Portrait</div>
          <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>Upload a real photo of your loved one (recommended) — AI will clean the background and create a dignified studio portrait that preserves their likeness. A written description alone works too.</p>

          <label className="label-dark">Reference photo (optional but recommended)</label>
          <input data-testid="reference-upload" type="file" accept="image/*" onChange={onReferencePhoto} className="input-dark" style={{ marginBottom: 12 }} />
          {referenceB64 && (
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
              <img src={`data:image/jpeg;base64,${referenceB64}`} alt="reference" style={{ width: 88, height: 88, borderRadius: 10, objectFit: "cover", border: "1px solid var(--border)" }} />
              <button data-testid="restore-btn" onClick={restoreReference} disabled={restoreBusy} className="btn-ghost" style={{ fontSize: 13 }}>{restoreBusy ? "Restoring…" : "✨ Restore / Colorize"}</button>
              <button onClick={() => setReferenceB64(null)} className="btn-ghost" style={{ fontSize: 13 }}>Remove reference</button>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <input data-testid="portrait-desc" className="input-dark" value={portraitDesc} onChange={e => setPortraitDesc(e.target.value)} placeholder="Additional notes (e.g., wearing a navy suit, kind smile)" style={{ flex: "1 1 280px" }} />
            <button data-testid="portrait-generate" onClick={generatePortrait} disabled={portraitBusy} className="btn-ghost">{portraitBusy ? "Painting…" : "Generate Portrait"}</button>
          </div>
          {portraitB64 && (
            <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center" }}>
              <img src={`data:image/png;base64,${portraitB64}`} alt="portrait" style={{ width: 160, height: 160, borderRadius: "50%", objectFit: "cover", border: "3px solid var(--gold)" }} />
              <button onClick={() => setPortraitB64(null)} className="btn-ghost" style={{ fontSize: 13 }}>Remove</button>
            </div>
          )}

          <div className="serif" style={{ fontSize: 22, color: "#fff", marginTop: 28, marginBottom: 12 }}>Photographs ({photos.length})</div>
          <input data-testid="photo-upload" type="file" accept="image/*" multiple onChange={onPhoto} className="input-dark" />
          {photos.length > 0 && (
            <>
              <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
                💡 Tap any photo to make it the <b style={{ color: "var(--gold)" }}>front cover hero</b>. You can change this any time.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10, marginTop: 14 }}>
                {photos.map((p, i) => {
                  const isCover = form.cover_photo_index === i;
                  return (
                    <div key={i} style={{ position: "relative" }}>
                      <img
                        src={`data:image/jpeg;base64,${p}`}
                        alt={`photo-${i}`}
                        data-testid={`photo-thumb-${i}`}
                        onClick={() => setForm({ ...form, cover_photo_index: isCover ? null : i })}
                        style={{
                          width: "100%", height: 120, objectFit: "cover", borderRadius: 8,
                          border: isCover ? "3px solid var(--gold)" : "1px solid var(--border)",
                          cursor: "pointer",
                          boxShadow: isCover ? "0 0 0 4px rgba(212,175,55,0.18)" : "none",
                          transition: "all 0.18s ease",
                        }}
                      />
                      {isCover && (
                        <div style={{
                          position: "absolute", top: 4, left: 4,
                          background: "var(--gold)", color: "#0a0a0b",
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                          padding: "3px 7px", borderRadius: 4,
                        }} data-testid={`photo-cover-badge-${i}`}>COVER ★</div>
                      )}
                      <button
                        onClick={() => {
                          removePhoto(i);
                          // If we deleted the cover photo OR shifted indexes, clear the cover selection.
                          if (isCover) setForm(f => ({ ...f, cover_photo_index: null }));
                          else if (form.cover_photo_index !== null && form.cover_photo_index > i) {
                            setForm(f => ({ ...f, cover_photo_index: f.cover_photo_index - 1 }));
                          }
                        }}
                        data-testid={`photo-remove-${i}`}
                        style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", borderRadius: 999, width: 24, height: 24, cursor: "pointer" }}
                      >×</button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 28, flexWrap: "wrap" }}>
            <button onClick={() => setStep(2)} className="btn-ghost">← Back</button>
            <button data-testid="new-save" onClick={save} disabled={busy} className="btn-gold">{busy ? "Saving…" : "Save Obituary ✓"}</button>
          </div>

          {/* Theme picker + memorial page settings */}
          <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid #1a1a1a" }}>
            <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>Cover heading</div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>The phrase printed on top of the cover page. The deceased's name appears below it.</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              {[
                "In Loving Memory",
                "Celebrating the Life of",
                "In Memoriam",
                "Honoring the Life of",
                "Forever Remembered",
                "A Life Well Lived",
                "Remembering",
              ].map(h => (
                <button
                  key={h}
                  data-testid={`cover-${h.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => setForm({ ...form, cover_heading: h })}
                  style={{
                    padding: "8px 14px", borderRadius: 999,
                    background: form.cover_heading === h ? "var(--gold)" : "transparent",
                    color: form.cover_heading === h ? "#0a0a0b" : "#ddd",
                    border: "1px solid " + (form.cover_heading === h ? "var(--gold)" : "#2a2a2a"),
                    cursor: "pointer", fontSize: 12,
                  }}
                >{h}</button>
              ))}
            </div>
            <input
              data-testid="cover-custom"
              value={form.cover_heading}
              onChange={e => setForm({ ...form, cover_heading: e.target.value })}
              placeholder="Or type your own"
              className="input-dark"
              style={{ width: "100%", marginBottom: 24 }}
            />

            <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>PDF theme</div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>Each theme uses a different color palette + symbol on the printed memorial booklet.</p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 22 }}>
              {themeOptions.map(t => (
                <button
                  key={t.id}
                  data-testid={`theme-${t.id}`}
                  onClick={() => setForm({ ...form, theme: t.id })}
                  style={{
                    padding: "12px 18px", borderRadius: 10,
                    background: form.theme === t.id ? t.color : "transparent",
                    color: form.theme === t.id ? "#0a0a0b" : "#ddd",
                    border: "1px solid " + (form.theme === t.id ? t.color : "#2a2a2a"),
                    cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
                  }}
                >{t.label}</button>
              ))}
            </div>

            <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>Cover beautification</div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
              Make the booklet cover gorgeous. Generate an AI decorative background tailored to your theme, and frame the portrait as a golden badge medallion.
            </p>

            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", color: "#ddd", padding: "12px 14px", background: "#0a0a0b", border: "1px solid #1f1f1f", borderRadius: 10, marginBottom: 12 }}>
              <input
                data-testid="badge-medallion-toggle"
                type="checkbox"
                checked={!!form.badge_medallion}
                onChange={e => setForm({ ...form, badge_medallion: e.target.checked })}
                style={{ accentColor: "var(--gold)" }}
              />
              <span style={{ fontSize: 14 }}>Render the cover portrait as a golden badge medallion (circular ring + ornament dots)</span>
            </label>

            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              <button
                data-testid="generate-cover-bg-btn"
                type="button"
                onClick={generateCoverBackground}
                disabled={coverBgBusy}
                className="btn-gold"
                style={{ padding: "10px 18px", fontSize: 13 }}
              >
                {coverBgBusy ? "Painting…" : (coverBgB64 ? "Regenerate cover background" : "Generate AI cover background")}
              </button>
              {coverBgB64 && (
                <>
                  <img
                    data-testid="cover-bg-preview"
                    src={`data:image/png;base64,${coverBgB64}`}
                    alt="Cover background preview"
                    style={{ width: 64, height: 80, objectFit: "cover", border: "1px solid #2a2a2a", borderRadius: 6 }}
                  />
                  <button
                    data-testid="cover-bg-clear"
                    type="button"
                    onClick={() => setCoverBgB64(null)}
                    className="btn-ghost"
                    style={{ padding: "8px 14px", fontSize: 12 }}
                  >Remove</button>
                </>
              )}
            </div>
            <div className="muted" style={{ fontSize: 11, marginBottom: 22 }}>Background uses your selected theme. Painted softly so text remains crisp.</div>

            <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 8 }}>Online memorial page</div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>Publish a public memorial page with a guestbook, candle lighting, and a QR code on the printed program.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <label className="label-dark">Funeral / service date</label>
                <input data-testid="funeral-date" type="date" className="input-dark" value={form.funeral_date} onChange={e => setForm({ ...form, funeral_date: e.target.value })} />
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Used for anniversary check-in emails.</div>
              </div>
              <div>
                <label className="label-dark">Family contact email</label>
                <input data-testid="family-email" type="email" className="input-dark" value={form.family_contact_email} onChange={e => setForm({ ...form, family_contact_email: e.target.value })} placeholder="family@example.com" />
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Receives guestbook + anniversary notes.</div>
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", color: "#ddd", padding: "12px 14px", background: "#0a0a0b", border: "1px solid #1f1f1f", borderRadius: 10 }}>
              <input data-testid="public-toggle" type="checkbox" checked={form.public_enabled} onChange={e => setForm({ ...form, public_enabled: e.target.checked })} style={{ accentColor: "var(--gold)" }} />
              <span style={{ fontSize: 14 }}>Publish a public memorial page (with QR code on PDF)</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
